import { Text, Code, Label, Icon, InlineStack, FormattedDateTime, Spinner } from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import type { SearchResult } from '@/services/auth/types';
import { useServicesQuery } from '@/services/api';
import type { ServicesApiNewsletter } from '@/services/api/services-api';
import { formatNumber } from '@/utils/number';
import { MetadataRow } from './metadata-row';

type Props = { result: Extract<SearchResult, { kind: 'newsletter' }> };

const CHANNEL_ICONS: Record<string, IconName> = {
  email: 'email',
  push: 'mobile',
  twilio: 'sms',
  in_app: 'in-app',
  webhook: 'webhook',
};

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  push: 'Push',
  twilio: 'SMS',
  in_app: 'In-App',
  webhook: 'Webhook',
};

export type SendState = 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'canceled';

export function deriveSendState(newsletter: ServicesApiNewsletter): SendState {
  if (newsletter.cancelled_at != null && newsletter.cancelled_at > 0) return 'canceled';
  if (newsletter.is_paused === true) return 'paused';
  if (newsletter.sent_at != null && newsletter.sent_at > 0) {
    return newsletter.sending === true ? 'sending' : 'sent';
  }
  if (newsletter.scheduled_at != null && newsletter.scheduled_at > 0) return 'scheduled';
  return 'draft';
}

const STATE_CONFIG: Record<SendState, { label: string; color: 'green' | 'grey' | 'yellow' }> = {
  draft: { label: 'Draft', color: 'grey' },
  scheduled: { label: 'Scheduled', color: 'grey' },
  sending: { label: 'Sending', color: 'green' },
  sent: { label: 'Sent', color: 'green' },
  paused: { label: 'Paused', color: 'yellow' },
  canceled: { label: 'Canceled', color: 'grey' },
};

export function NewsletterPreview({ result }: Props) {
  const envId = Number(result.other.env_id);
  const newsletterId = Number(result.other.id);

  const { data, isLoading, isError } = useServicesQuery(
    'getNewsletterById',
    { params: { environment_id: envId, id: newsletterId } },
    { enabled: envId > 0 && newsletterId > 0, staleTime: 5 * 60 * 1000 },
  );

  const newsletter = data?.newsletter;
  const isSent = result.other.sent === 'true';
  const hasFetchedDetails = newsletter != null && !isError;

  const channel = newsletter?.channel;
  const channelIcon = channel != null ? CHANNEL_ICONS[channel] : undefined;
  const channelLabel = channel != null ? CHANNEL_LABELS[channel] : undefined;

  const sendState = hasFetchedDetails ? deriveSendState(newsletter) : isSent ? 'sent' : 'draft';
  const stateConfig = STATE_CONFIG[sendState];

  const hasSentMetrics =
    sendState === 'sent' || sendState === 'sending' || sendState === 'canceled' || sendState === 'paused';

  return (
    <>
      <MetadataRow label="ID">
        <Code size="xs">{result.other.id}</Code>
      </MetadataRow>
      {isLoading ? (
        <MetadataRow label="Channel">
          <Spinner />
        </MetadataRow>
      ) : isError ? (
        <MetadataRow label="Channel">
          <Text size="xs" color="subtle">
            Failed to load details
          </Text>
        </MetadataRow>
      ) : channelLabel != null ? (
        <MetadataRow label="Channel">
          <InlineStack gap="050" alignBlock="center">
            {channelIcon != null ? <Icon name={channelIcon} size="xs" sizeVersion="v2" color="subtle" /> : null}
            <Text size="xs">{channelLabel}</Text>
          </InlineStack>
        </MetadataRow>
      ) : null}
      <MetadataRow label="Status">
        <Label color={stateConfig.color}>{stateConfig.label}</Label>
      </MetadataRow>
      {newsletter?.sent_at != null && newsletter.sent_at > 0 ? (
        <MetadataRow label="Sent at">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={newsletter.sent_at} format="medium" />
          </Text>
        </MetadataRow>
      ) : null}
      {hasSentMetrics && newsletter?.total_sent != null ? (
        <MetadataRow label="Sent">
          <Text size="xs">{formatNumber(newsletter.total_sent)}</Text>
        </MetadataRow>
      ) : null}
      {hasSentMetrics && newsletter?.total_delivered != null ? (
        <MetadataRow label="Delivered">
          <Text size="xs">{formatNumber(newsletter.total_delivered)}</Text>
        </MetadataRow>
      ) : null}
      <MetadataRow label="Created">
        <Text size="xs">
          <FormattedDateTime className="fs-mask" date={result.other.created_at} format="medium" />
        </Text>
      </MetadataRow>
      <MetadataRow label="Updated">
        <Text size="xs">
          <FormattedDateTime className="fs-mask" date={result.other.updated_at} format="medium" />
        </Text>
      </MetadataRow>
    </>
  );
}
